// Share event link.
// Native (Android): Capacitor Share plugin → system share sheet.
// Web fallback: navigator.share when available, otherwise clipboard.

import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Clipboard } from '@capacitor/clipboard';

const isNative = () => Capacitor.isNativePlatform?.() === true;

// Use a ROOT query-param URL (not /event/<id>). The web build uses relative
// asset paths (homepage: "." for Capacitor), so a nested path like
// /event/<id> would 404 the JS/CSS and render a blank page. ?event= keeps the
// page at "/" where assets resolve correctly. The app reads ?event= on load
// and the native deep-link handler also accepts it.
const buildShareUrl = (eventId) =>
  `https://linksy.community/?event=${encodeURIComponent(eventId)}`;

export const shareEvent = async (eventId) => {
  const shareUrl = buildShareUrl(eventId);

  if (isNative()) {
    try {
      await Share.share({
        title: 'Check out this event on Linksy!',
        text: 'Join this event on Linksy',
        url: shareUrl,
        dialogTitle: 'Share event',
      });
      return;
    } catch (err) {
      // User cancelled the share sheet — silent.
      if (String(err?.message || err).toLowerCase().includes('cancel')) return;
      console.error('Native share error:', err);
      // Fall through to clipboard copy
    }
    try {
      await Clipboard.write({ string: shareUrl });
      window.showToast?.('Link copied to clipboard!', 'success');
    } catch (e) { console.error('Native clipboard error:', e); }
    return;
  }

  // Web path
  if (navigator.share) {
    try {
      await navigator.share({
        title: 'Check out this event on Linksy!',
        text: 'Join this event on Linksy',
        url: shareUrl,
      });
      return;
    } catch (err) {
      if (err?.name === 'AbortError') return;
    }
  }

  try {
    await navigator.clipboard.writeText(shareUrl);
    window.showToast?.('Link copied to clipboard!', 'success');
  } catch {
    const textArea = document.createElement('textarea');
    textArea.value = shareUrl;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    window.showToast?.('Link copied to clipboard!', 'success');
  }
};

window.shareEvent = shareEvent;
